import React, { useState, useMemo, useCallback, useContext } from 'react';
import styled from 'styled-components';
import {
  flex,
  FlexColumn,
  FlexColumnCenter,
  Modal,
  RadioButton,
  TimePicker as BaseTimePicker
} from '@ubeya/shared-web/components';
import useAccountConfig from '@ubeya/shared/hooks/account/useAccountConfig';
import ConfigContext from '@ubeya/shared-web/contexts/ConfigContext';

const Wrapper = styled(FlexColumnCenter)`
  margin: 20px 40px;
  align-items: flex-start;
`;

const Buttons = styled(FlexColumn)`
  > div:not(:last-child) {
    margin-bottom: 30px;
  }
`;

const Time = styled.div`
  display: flex;
  flex-direction: 'row';
`;

const TimeContainer = styled.div``;

const TimePicker = styled(BaseTimePicker).attrs(() => ({ className: 'time-picker' }))`
  width: 100px;
  border-bottom: 1px solid black;
  line-height: 1;
  padding: 0;

  &.open {
    display: flex !important;
  }

  .select {
    border: none;
    background: transparent;
    box-shadow: none;
  }

  .select-container {
    padding-right: 0;
    display: flex;
    justify-content: center;
  }

  .select-arrow {
    display: none;
  }

  .select-input,
  .select-label {
    padding: 0;
    min-height: 0;
  }

  .select-label {
    ${flex};
    max-width: unset;
    font-size: 15px;
    line-height: unset;
    overflow: unset !important;
    color: ${({ theme }) => theme.colors.black};
  }

  .select-menu {
    margin-top: -8px;
  }
`;

const Splitter = styled.div`
  width: 20px;
  display: flex;
  justify-content: center;
  margin-bottom: 2px;
`;

const TYPES_MAP = {
  Available: 'available',
  'Not Available': 'notAvailable',
  Partial: 'specific'
};

const AddGeneralAvailabilityModal = ({
  onClose,
  data,
  selectedDay,
  updateGeneralAvailabilities,
  availabilitiesByEmployee
}) => {
  const { t, unavailableByDefault } = useContext(ConfigContext);
  const { type, start, end } = data || {};
  const [availabilityStatus, setAvailabilityStatus] = useState(
    type ? TYPES_MAP[type] : unavailableByDefault ? 'notAvailable' : 'available'
  );
  const [isLoading, setIsLoading] = useState(false);
  const [specificAvailabilityHours, setSpecificAvailabilityHours] = useState({ start, end });
  const day = selectedDay.index;

  const {
    account: { hourFormat }
  } = useAccountConfig();

  const onConfirm = useCallback(() => {
    setIsLoading(true);

    const { board: currentBoard = [] } = availabilitiesByEmployee || {};
    let newItem;

    if (availabilityStatus === 'available') {
      newItem = { day, type: 'Available' };
    }

    if (availabilityStatus === 'notAvailable') {
      newItem = { day, type: 'Not Available' };
    }

    if (availabilityStatus === 'specific') {
      newItem = {
        day,
        type: 'Partial',
        start: specificAvailabilityHours.start,
        end: specificAvailabilityHours.end
      };
    }

    const board = currentBoard.some(({ day: itemDay }) => itemDay === day)
      ? currentBoard.map((item) => {
          if (item.day !== day) {
            return item;
          }
          return newItem;
        })
      : [...currentBoard, newItem];

    updateGeneralAvailabilities({ board });

    onClose();
    setIsLoading(false);
  }, [
    availabilitiesByEmployee,
    availabilityStatus,
    day,
    onClose,
    specificAvailabilityHours,
    updateGeneralAvailabilities
  ]);

  const disabledConfirm = useMemo(() => {
    if (availabilityStatus === 'specific' && (!specificAvailabilityHours?.start || !specificAvailabilityHours?.end)) {
      return true;
    }
    return false;
  }, [availabilityStatus, specificAvailabilityHours?.end, specificAvailabilityHours?.start]);

  return (
    <Modal
      onClose={onClose}
      showConfirmButton
      confirmText={t('save')}
      disabledConfirm={disabledConfirm}
      loadingConfirm={isLoading}
      isPaddingContent={false}
      size="small"
      title={`${t(selectedDay?.title)} - ${t('availabilty')}`}
      onConfirm={onConfirm}
      prompt={false}>
      <Wrapper>
        <Buttons>
          <RadioButton
            id="available"
            value={availabilityStatus}
            onChange={setAvailabilityStatus}
            content={t('availableAllDay')}
          />

          <RadioButton
            id="notAvailable"
            value={availabilityStatus}
            onChange={setAvailabilityStatus}
            content={t('notAvailableAllDay')}
          />

          <RadioButton
            id="specific"
            value={availabilityStatus}
            onChange={setAvailabilityStatus}
            content={t('availableSpecific')}
          />

          {availabilityStatus === 'specific' && (
            <TimeContainer>
              <Time>
                <TimePicker
                  value={specificAvailabilityHours?.start || ''}
                  onChange={(newTime) => {
                    setSpecificAvailabilityHours({
                      start: newTime,
                      end: specificAvailabilityHours.end
                    });
                  }}
                  onOutsideClickSave
                  $hourFormat={hourFormat}
                  placeholder={t('start')}
                  usePortal
                />
                <Splitter>-</Splitter>
                <TimePicker
                  value={specificAvailabilityHours?.end || ''}
                  onChange={(newTime) => {
                    setSpecificAvailabilityHours({
                      end: newTime,
                      start: specificAvailabilityHours.start
                    });
                  }}
                  onOutsideClickSave
                  $hourFormat={hourFormat}
                  placeholder={t('end')}
                  usePortal
                />
              </Time>
            </TimeContainer>
          )}
        </Buttons>
      </Wrapper>
    </Modal>
  );
};
export default AddGeneralAvailabilityModal;
